import type { Action, Computed, Thunk } from 'easy-peasy';
import { action, computed, thunk } from 'easy-peasy';
import type { Injections } from '../services/injections';
import type {
  AssetActionPayloadResult,
  AssetState,
  AssetTypes,
  WorkflowAsset,
  WorkflowActionError,
  WorkflowGroupStatusResponse,
  WorkflowRequest,
  WorkflowResponse,
  WorkflowStatusResponse,
  WorkflowGroupOptions,
  Asset,
  WorkflowGroupCategory,
} from './types';
import type { Workflow } from './Workflow';
import type { WorkflowGroup } from './WorkflowGroup';
import workflowGroupManager from './definitions';
import deepmerge from 'deepmerge';
import { AssetWorkflow } from './AssetWorkflow';
import type { EditAssetWorkflow } from './EditAssetWorkflow';
import type { StoreModel } from '..';
import type { TenantModel } from '../assets/tenants';
import { AssetService } from '../services/AssetService';
import type { TenantLink } from '../assets/types';
import { ALL_TENANTS, GLOBAL_TENANT } from '../../components/workflow/tenant/GlobalTenantSelect';
import type { CarrierServiceMethodInfo, PartnerTenant } from '@shipium-corp/shipium-ts-types';
import { trackWorkflowSave } from '~/utils/analytics/userActionAnalytics';
import { deriveIsShipiumAdmin } from './deriveIsShipiumAdmin';

export interface WorkflowModel {
  WorkflowActionStart: Action<WorkflowModel, void>;
  isSaving: boolean;
  setIsSaving: Action<WorkflowModel, boolean>;
  lastAction?: number;
  workflowGroups: Record<string, WorkflowGroup>;
  workflowConfig: WorkflowGroupOptions[];
  setWorkflowFormatter: Action<WorkflowModel, { workflowKey: string; formatter: (val: any) => any }>;
  workflowFormatters: Record<string, (val: any) => any>;
  workflows: Record<string, Workflow>;
  workflowAssetURNs: Record<string, any>;
  configurationWorkflows: Computed<WorkflowModel, (workflowGroupKey?: string) => Workflow[]>;
  addAssetWorkflows: Computed<WorkflowModel, AssetWorkflow[]>;
  editAssetWorkflows: Computed<WorkflowModel, EditAssetWorkflow[]>;
  editAssetWorkflowAssetTypes: Computed<WorkflowModel, (workflowGroupKey: string) => WorkflowAsset[]>;
  editAssetWorkflowAssetTypesForAssetId: Computed<WorkflowModel, (assetId: string) => EditAssetWorkflow | undefined>;
  getEditWorkflowsForAssetId: Computed<WorkflowModel, (assetId: string) => EditAssetWorkflow[]>;
  getLinkedAssetsForAssetUrn: Computed<WorkflowModel, (assetUrn: string) => Asset[]>;
  getAddAssetWorkflowForWorkflowGroupKey: Computed<
    WorkflowModel,
    (workflowGroupKey: string) => AssetWorkflow | undefined
  >;
  getEditWorkflowForAssetUrn: Computed<WorkflowModel, (assetId: string) => EditAssetWorkflow | undefined>;
  workflowState: object;
  workflowAssets: Computed<WorkflowModel, AssetState>;
  workflowAssetsCache: AssetState;
  workflowResponseForCurrentState: Computed<
    WorkflowModel,
    (workflowKey: string, workflowStepKey?: string) => WorkflowResponse | undefined
  >;
  workflowStatus: Computed<WorkflowModel, (workflowKey: string) => WorkflowStatusResponse>;
  workflowGroupStatus: Computed<WorkflowModel, (workflowGroupKey: string) => WorkflowGroupStatusResponse>;
  workflowGroupByCategory: Computed<WorkflowModel, (workflowGroupCategory: WorkflowGroupCategory) => WorkflowGroup[]>;
  fetchWorkflows: Thunk<WorkflowModel, boolean | void, Injections, StoreModel, Promise<void>>;
  rerenderWorkflows: Thunk<WorkflowModel, void, Injections, StoreModel, Promise<void>>;
  refreshWorkflow: Thunk<WorkflowModel, string, Injections, StoreModel, Promise<void>>;
  fetchAssetsByAssetType: Thunk<WorkflowModel, AssetTypes[], Injections, StoreModel, Promise<void>>;
  fetchAssetsForWorkflow: Thunk<WorkflowModel, string, Injections, WorkflowModel, Promise<void>>;
  fetchAssetsForAllWorkflows: Thunk<WorkflowModel, void, Injections, WorkflowModel, Promise<void>>;
  handle: Thunk<WorkflowModel, WorkflowRequest, Injections, StoreModel, Promise<undefined | WorkflowResponse>>;
  saveWorkflow: Thunk<
    WorkflowModel,
    WorkflowRequest,
    Injections,
    StoreModel,
    Promise<AssetActionPayloadResult | undefined>
  >;
  saveWorkflowFailure: Action<WorkflowModel, WorkflowActionError | undefined>;
  workflowActionError: WorkflowActionError | undefined;
  setWorkflows: Action<WorkflowModel, Record<string, Workflow>>;
  updateWorkflows: Action<WorkflowModel, Workflow[]>;
  setWorkflowGroups: Action<WorkflowModel, Record<string, WorkflowGroup>>;
  setWorkflowState: Action<WorkflowModel, object>;
  setWorkflowAssetState: Action<WorkflowModel, Partial<AssetState>>;
  mergeWorkflowState: Action<WorkflowModel, object>;
  hideContinueButton: boolean;
  setHideContinueButton: Action<WorkflowModel, boolean>;
  skipWorkflowStep: Action<WorkflowModel, { workflowGroupKey: string; workflowKey: string; workflowStepKey: string }>;
}
const filterAssets = (key: AssetTypes, assets: any[], links: Record<string, TenantLink[]>) => {
  const urlKey = AssetService.assetTypeToAssetUrlName(key);
  const assetIdKey = AssetWorkflow.getAssetIdKeyForAssetType(key);
  if (!(urlKey in links)) {
    return [];
  }
  const ids = links[urlKey].reduce<Set<string>>((prev, curr) => {
    prev.add(curr.shipiumId);
    return prev;
  }, new Set<string>());
  return assets.filter((asset) => ids.has(asset[assetIdKey]));
};
const getAllTenantScopedAssets = (tenants: Record<string, PartnerTenant>) => {
  return Object.values(tenants).reduce((linkSet, val) => {
    return new Set([...linkSet, ...Object.values(val.links!).flatMap((list) => list.map((link) => link.shipiumId))]);
  }, new Set<string>());
};
export const workflowModel: WorkflowModel = {
  isSaving: false,
  hideContinueButton: false,
  workflowFormatters: {},
  workflowAssetURNs: {},
  workflows: {},
  workflowGroups: {},
  setIsSaving: action((state, payload) => {
    state.isSaving = payload;
  }),
  editAssetWorkflows: computed((state) => {
    const editAssets = Object.values(state.workflows).filter((wf) => {
      return wf.workflowType === 'editAssetWorkflow';
    }) as EditAssetWorkflow[];
    return editAssets.sort((assetA, assetB) =>
      new Date(assetA.asset?.auditInfo?.lastUpdatedTimeStamp ?? '') >
      new Date(assetB.asset?.auditInfo?.lastUpdatedTimeStamp ?? '')
        ? -1
        : 1,
    );
  }),
  addAssetWorkflows: computed(
    (state) => Object.values(state.workflows).filter((wf) => wf.workflowType === 'addAssetWorkflow') as AssetWorkflow[],
  ),
  workflowActionError: undefined,
  workflowState: workflowGroupManager.initializeWorkflowState(),
  workflowAssets: computed(
    [(state) => state.workflowAssetsCache, (state: any, storeState: any) => storeState.tenant],
    (...args) => {
      const [workflowAssetsCache, tenant] = args as [AssetState, TenantModel];
      const workflowAssetsTemp = {} as AssetState;
      workflowAssetsTemp.carrier = workflowAssetsCache.carrier as any[];
      workflowAssetsTemp.carrierServiceMethod = workflowAssetsCache.carrierServiceMethod as any[];
      workflowAssetsTemp.tenant = workflowAssetsCache.tenant as any[];
      const allLinks = getAllTenantScopedAssets(tenant.tenants);
      if (
        tenant.globallySelectedTenant &&
        tenant.globallySelectedTenant !== ALL_TENANTS &&
        tenant.tenants[tenant.globallySelectedTenant]
      ) {
        const links = tenant.tenants[tenant.globallySelectedTenant].links!;
        Object.keys(workflowAssetsCache).forEach((skey) => {
          const key = skey as keyof AssetState;
          if (['carrier', 'carrierServiceMethod', 'tenant'].includes(key.toString())) {
            return;
          }
          const global = workflowAssetsCache[key].filter(
            (asset) => !allLinks.has(asset[AssetWorkflow.getAssetIdKeyForAssetType(key)]),
          );
          const nonGlobal = workflowAssetsCache[key].filter((asset) =>
            allLinks.has(asset[AssetWorkflow.getAssetIdKeyForAssetType(key)]),
          );
          workflowAssetsTemp[key] = [...filterAssets(key, nonGlobal, links), ...global];
        });
      } else if (tenant.globallySelectedTenant === GLOBAL_TENANT) {
        Object.keys(workflowAssetsCache).forEach((skey) => {
          const key = skey as keyof AssetState;
          workflowAssetsTemp[key] = workflowAssetsCache[key].filter(
            (asset) => !allLinks.has(asset[AssetWorkflow.getAssetIdKeyForAssetType(key)]),
          );
        });
      } else {
        // for in loops get a warning from eslint for I would rather just
        // avoid them. There is nothing wrong with the old code though
        Object.keys(workflowAssetsCache).forEach((skey) => {
          const key = skey as keyof AssetState;
          workflowAssetsTemp[key] = workflowAssetsCache[key] as any[];
        });
      }
      return workflowAssetsTemp;
    },
  ),
  workflowAssetsCache: workflowGroupManager.initializeWorkflowAssets(),
  workflowConfig: workflowGroupManager.options,
  setHideContinueButton: action((state, payload) => {
    state.hideContinueButton = payload;
  }),
  WorkflowActionStart: action((state, payload) => {
    state.lastAction = Date.now();
  }),
  fetchAssetsByAssetType: thunk(async (actions, payload, { getStoreActions, injections, getStoreState }) => {
    const assetState: Record<string, any[]> = {};
    const storeActions = getStoreActions();
    const allPromises = await Promise.all(
      payload.map(async (assetType) => {
        // TODO add a assetService helper
        if (assetType === 'carrier') {
          return injections.UIOrchestrationService.fetchCarriers();
        }
        if (assetType === 'carrierServiceMethod') {
          // todo fix this...
          const carriers = await injections.UIOrchestrationService.fetchCarriers();
          if (!carriers) {
            return [];
          }
          return carriers.reduce<CarrierServiceMethodInfo[]>((prev, curr) => {
            prev.push(...curr.serviceMethods);
            return prev;
          }, []);
        }
        if (assetType === 'thirdPartyBillingAccountSet') {
          return storeActions.thirdPartyBilling.fetchThirdPartyBillingAccountSets({});
        }
        if (assetType === 'thirdPartyBillingAccount') {
          return storeActions.thirdPartyBilling.fetchThirdPartyBillingAccounts({});
        }
        if (assetType === 'tenant') {
          return storeActions.tenant.fetchTenants({});
        }
        if (assetType === 'returnsSettings') {
          return storeActions.returnsSettings.fetchReturnsSettings({});
        }
        if (assetType === 'origin') {
          return storeActions.origins.fetchOrigins({});
        }
        if (assetType === 'fulfillmentContext') {
          return storeActions.fulfillmentContext.fetchFulfillmentContexts({});
        }
        if (assetType === 'tags') {
          return storeActions.tags.fetchTagsByCriteria(undefined);
        }
        if (assetType === 'address') {
          return storeActions.address.fetchAddresses({});
        }
        if (assetType === 'schedule') {
          return storeActions.schedule.fetchSchedules({});
        }
        if (assetType === 'contact') {
          return storeActions.contact.fetchContacts({});
        }
        if (assetType === 'packaging') {
          return storeActions.packaging.fetchPackagings({});
        }
        if (assetType === 'packagingSet') {
          return storeActions.packaging.fetchPackagingSets({});
        }
        if (assetType === 'carrierAccount') {
          return storeActions.carrierAccounts.fetchCarrierAccounts({});
        }
        if (assetType === 'augmentationSet') {
          return storeActions.labelAugmentations.fetchLabelAugmentationSets({});
        }
        if (assetType === 'carrierSettings') {
          return storeActions.carrierSettings.fetchCarrierSettings({});
        }
        if (assetType === 'labelProperties') {
          return storeActions.labelProperties.fetchLabelProperties({});
        }
        if (assetType === 'contract') {
          return storeActions.contract.fetchContracts({});
        }
        if (assetType === 'labelAugmentation') {
          return storeActions.labelAugmentations.fetchLabelAugmentations({});
        }
        if (assetType === 'fulfillmentContextLinkProperty') {
          return storeActions.fulfillmentContextLinkProperties.fetchFulfillmentContextLinkProperties({});
        }
        if (assetType === 'returnAddressOverride') {
          return storeActions.returnToAddressOverrides.fetchReturnToAddressOverrides({});
        }
        if (assetType === 'laneSortConfigurations') {
          return storeActions.FP_fulfillmentContext.fetchDockLaneSortConfigurations(undefined);
        }
      }),
    );
    payload.forEach((assetType, index) => {
      const result = allPromises[index];
      if (result) {
        // assumes the convention of assetType == assetState property name
        assetState[assetType] = result;
      }
    });
    actions.setWorkflowAssetState(assetState);
  }),
  fetchWorkflows: thunk(async (actions, forceRefetch, { getState, getStoreState, injections }) => {
    const workflowKeys = Object.entries(getState().workflowAssetsCache)
      .map(([key, value]) => ({ key, length: value.length }))
      .filter(({ key, length }) => length > 0 && key !== 'carrierRate' && key !== 'carrierRateSet');

    if (!forceRefetch && workflowKeys.length > 0) {
      actions.saveWorkflowFailure(undefined);
      return;
    }
    actions.WorkflowActionStart();
    await actions.fetchAssetsForAllWorkflows();
    const workflowAssets = getState().workflowAssets;
    const globalState = getStoreState();

    const workflows = workflowGroupManager.buildWorkflows(
      workflowAssets,
      globalState.partner.activePartner?.featureToggles || {},
      deriveIsShipiumAdmin(globalState.session.user?.email),
    );
    const workflowGroupMap = workflowGroupManager.workflowGroupMap;
    actions.setWorkflows(workflows);
    actions.setWorkflowGroups(workflowGroupMap);
  }),
  skipWorkflowStep: action((state, payload) => {
    const { workflowKey, workflowStepKey } = payload;
    const step = state.workflows[workflowKey].workflowStepMap.get(workflowStepKey);
    if (!step) {
      throw new Error('Unable to skip step');
    }
    step.setSkipped(true);
  }),
  rerenderWorkflows: thunk(async (actions, payload, { getState, getStoreState, injections }) => {
    const workflowAssets = getState().workflowAssets;
    const globalState = getStoreState();

    const workflows = workflowGroupManager.buildWorkflows(
      workflowAssets,
      globalState.partner.activePartner?.featureToggles || {},
      deriveIsShipiumAdmin(globalState.session.user?.email),
    );
    const workflowGroupMap = workflowGroupManager.workflowGroupMap;
    actions.setWorkflows(workflows);
    actions.setWorkflowGroups(workflowGroupMap);
  }),
  refreshWorkflow: thunk(async (actions, workflowKey, { getState, getStoreState, injections }) => {
    actions.WorkflowActionStart();
    const workflows = getState().workflows;
    const workflow = workflows[workflowKey];
    if (!workflow) {
      throw new Error('Unexpected Refresh Workflow Error');
    }
    await actions.fetchAssetsForWorkflow(workflowKey);
    const updatedWorkflows = workflowGroupManager.workflowGroupMap[workflow.workflowGroupKey].buildWorkflows(
      getState().workflowAssets,
    );
    actions.updateWorkflows(updatedWorkflows);
  }),
  fetchAssetsForWorkflow: thunk(async (actions, payload, { getState, injections }) => {
    const workflows = getState().workflows;
    const workflow = workflows[payload];
    if (!workflow) {
      throw new Error('Unexpected Fetch Assets Workflow Error');
    }
    const assetTypes = workflow.workflowAssets || [];
    // ensure that we fetch the edit asset type regardless of workflowAssets
    if (workflow.workflowType === 'editAssetWorkflow' || workflow.workflowType === 'addAssetWorkflow') {
      const editWorkflow = workflow as EditAssetWorkflow;
      if (!assetTypes.includes(editWorkflow.assetType)) {
        assetTypes.push(editWorkflow.assetType);
      }
    }
    if (assetTypes.length === 0) {
      return;
    }
    await actions.fetchAssetsByAssetType(assetTypes);
  }),
  fetchAssetsForAllWorkflows: thunk(async (actions, payload, { getState, injections }) => {
    // todo derive this from edit workflow options
    actions.WorkflowActionStart();
    const assetTypes: AssetTypes[] = [
      'carrier',
      'carrierServiceMethod',
      'origin',
      'schedule',
      'carrierAccount',
      'address',
      'contact',
      'carrierSettings',
      'contract',
      'fulfillmentContext',
      'fulfillmentContextLinkProperty',
      'labelProperties',
      'labelAugmentation',
      'tags',
      'packaging',
      'packagingSet',
      'augmentationSet',
      'returnsSettings',
      'tenant',
      'thirdPartyBillingAccount',
      'thirdPartyBillingAccountSet',
      'returnAddressOverride',
      'laneSortConfigurations',
    ];
    await actions.fetchAssetsByAssetType(assetTypes);
  }),
  setWorkflows: action((state, payload) => {
    state.workflows = payload;
    const editAssetWorkflows = Object.values(state.workflows).filter(
      (workflow) => workflow.workflowType === 'editAssetWorkflow',
    ) as EditAssetWorkflow[];
    for (const editWorkflow of editAssetWorkflows) {
      (state.workflowState as any)[editWorkflow.workflowKey] = editWorkflow.mapAssetInitialWorkflowState();
    }
  }),
  updateWorkflows: action((state, payload) => {
    for (const workflow of payload) {
      workflowGroupManager.workflowMap[workflow.workflowKey] = workflow;
      state.workflows[workflow.workflowKey] = workflow;
    }
  }),
  setWorkflowGroups: action((state, payload) => {
    state.workflowGroups = payload;
  }),
  setWorkflowState: action((state, payload) => {
    state.workflowState = payload;
  }),
  setWorkflowAssetState: action((state, payload) => {
    let key: keyof AssetState;

    for (key in payload) {
      if (key && payload[key]) {
        const assets = payload[key] as any[];
        state.workflowAssetsCache[key] = assets;
        if (Array.isArray(assets)) {
          for (const asset of assets) {
            const id = asset.urn || asset.carrierId || asset.serviceMethodId;
            state.workflowAssetURNs[id] = asset;
          }
        }
      }
    }
  }),
  mergeWorkflowState: action((state, payload) => {
    state.workflowActionError = undefined;
    const overwriteMerge = (destinationArray: any[], sourceArray: any) => sourceArray;
    const merged = deepmerge<object, object>(state.workflowState, payload, { arrayMerge: overwriteMerge });
    state.workflowState = merged;
  }),
  workflowStatus: computed((state) => (workflowKey) => {
    return state.workflows[workflowKey].status({
      workflowKey,
      workflowState: state.workflowState,
      workflowAssets: state.workflowAssets,
    });
  }),
  workflowGroupStatus: computed((state) => (workflowGroupKey) => {
    return state.workflowGroups[workflowGroupKey].status({
      workflowKey: '',
      workflowState: state.workflowState,
      workflowAssets: state.workflowAssets,
    });
  }),
  workflowResponseForCurrentState: computed((state) => (workflowKey, workflowStepKey) => {
    const workflow = state.workflows[workflowKey];
    if (!workflow) {
      return;
    }
    return workflow.handle({
      workflowKey,
      workflowStepKey,
      workflowState: state.workflowState,
      workflowAssets: state.workflowAssets,
    });
  }),
  configurationWorkflows: computed((state) => (workflowGroupKey) => {
    return Object.values(state.workflows).filter((wf) => {
      return (
        (!workflowGroupKey || wf.workflowGroupKey === workflowGroupKey) && wf.workflowType === 'configurationWorkflow'
      );
    });
  }),
  workflowGroupByCategory: computed((state) => (workflowGroupCategory) => {
    return Object.values(state.workflowGroups)
      .filter(
        (group) =>
          group.options.workflowGroupCategory === workflowGroupCategory &&
          group.options.excludeFromConfigurationIndex !== true,
      )
      .sort((a, b) => a.workflowGroupIndex - b.workflowGroupIndex);
  }),
  editAssetWorkflowAssetTypes: computed((state) => (workflowGroupKey) => {
    const editWorkflowsForGroupKey = state.editAssetWorkflows.filter((wf) => wf.workflowGroupKey === workflowGroupKey);
    return Object.values(
      editWorkflowsForGroupKey.reduce<Record<string, WorkflowAsset>>((prev, curr) => {
        if (!curr.archived) {
          prev[curr.assetId] = {
            assetId: curr.assetId,
            assetType: curr.assetType,
            assetIdKey: curr.assetIdKey,
            active: curr.active,
            archived: curr.archived,
            workflow: curr,
          };
        }
        return prev;
      }, {}),
    );
  }),
  editAssetWorkflowAssetTypesForAssetId: computed((state) => (assetId) => {
    return state.editAssetWorkflows.find((wf) => wf.assetId === assetId);
  }),
  getEditWorkflowsForAssetId: computed((state) => (assetId) => {
    return state.editAssetWorkflows.filter((workflow) => {
      return workflow.assetId === assetId && workflow.enabled === true;
    });
  }),
  getAddAssetWorkflowForWorkflowGroupKey: computed((state) => (workflowGroupKey) => {
    return state.addAssetWorkflows.find((wf) => wf.workflowGroupKey === workflowGroupKey);
  }),
  getEditWorkflowForAssetUrn: computed((state) => (urn) => {
    return state.editAssetWorkflows.find((wf) => wf.asset.urn === urn);
  }),
  getLinkedAssetsForAssetUrn: computed((state) => (urn) => {
    const asset = state.workflowAssetURNs[urn];
    const urnParts = urn?.split(':');
    const assetType = urnParts?.length > 1 ? (urnParts[1] as AssetTypes) : undefined;

    if (!asset || !assetType) {
      return [];
    }

    const linkProperties = AssetWorkflow.getLinkPropertiesForAssetType(assetType) || {};

    return Object.keys(linkProperties).reduce<Asset[]>((prev, curr) => {
      const linkAssetType = linkProperties[curr];
      const prop = curr;

      if (prop) {
        const assetLinkId = asset[prop];
        const assetIdKey = AssetWorkflow.getAssetIdKeyForAssetType(linkAssetType);

        if (Array.isArray(assetLinkId)) {
          const linkedAssets = state.workflowAssets[linkAssetType].filter((a) => assetLinkId.includes(a[assetIdKey]));
          if (linkedAssets.length > 0) {
            prev.push(...linkedAssets);
          }
        } else {
          const linkedAsset = state.workflowAssets[linkAssetType].find((a) => a[assetIdKey] === assetLinkId);
          if (linkedAsset) {
            prev.push(linkedAsset);
          } else {
            console.log(urn, linkProperties, linkedAsset, assetLinkId);
          }
        }
      }
      return prev;
    }, []);
  }),
  handle: thunk(async (actions, payload, { getState, injections, getStoreActions, getStoreState }) => {
    try {
      const workflow = getState().workflows[payload.workflowKey];
      if (!workflow) {
        return;
      }
      // if you change locations stop hiding the continue button
      actions.setHideContinueButton(false);
      const workflowResponse = workflow.handle({
        workflowKey: payload.workflowKey,
        workflowStepKey: payload.workflowFromStepKey,
        workflowFromStepKey: payload.workflowFromStepKey,
        workflowState: getState().workflowState,
        workflowAssets: getState().workflowAssets,
      });
      // TODO add a flag to workflow reponse to indicate that a save should occur
      if (workflowResponse.workflowComponent?.workflowComponentType === 'workflowSummary') {
        const assetWorkflow = workflow as AssetWorkflow;
        const assetActionPayload = assetWorkflow.buildAssetActionPayload({
          workflowKey: payload.workflowKey,
          workflowStepKey: payload.workflowFromStepKey,
          workflowFromStepKey: payload.workflowFromStepKey,
          workflowState: getState().workflowState,
          workflowAssets: getState().workflowAssets,
        });
        // todo dispatch this to the asset service
        console.log(assetActionPayload);
      }
      return workflowResponse;
    } catch (error) {
      throw new Error('Unexpected Workflow Error');
    }
  }),
  saveWorkflow: thunk(async (actions, payload, { getState, getStoreState, injections, getStoreActions }) => {
    try {
      actions.setIsSaving(true);
      actions.WorkflowActionStart();
      const workflow = getState().workflows[payload.workflowKey];
      if (!workflow) {
        return;
      }
      const assetWorkflow = workflow as EditAssetWorkflow;
      const assetActionPayload = assetWorkflow.buildAssetActionPayload({
        workflowKey: payload.workflowKey,
        workflowStepKey: payload.workflowFromStepKey,
        workflowFromStepKey: payload.workflowFromStepKey,
        workflowState: getState().workflowState,
        workflowAssets: getState().workflowAssets,
      });
      assetActionPayload.assetPayload = assetWorkflow.workflowStateTransformFunction(
        assetActionPayload.assetPayload,
        assetWorkflow.asset,
      );

      trackWorkflowSave({
        actionType: assetActionPayload?.assetActionType || 'unknown',
        assetType: assetActionPayload?.assetType || 'unknown',
        assetName: assetWorkflow?.asset?.name,
        metadata: {
          assetActionPayload,
          currentAssetDetails: assetWorkflow?.asset,
        },
      });
      const result = await injections.assetService.handleAssetActionPayload(assetActionPayload);

      // Invalidate queries after successful asset operation
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'workflow-update',
        entityId: payload.workflowKey,
      });

      await actions.fetchWorkflows(true);
      if (result.error) {
        actions.saveWorkflowFailure(result.error);
      }
      actions.setIsSaving(false);
      return result;
    } catch (error) {
      actions.setIsSaving(false);
      throw new Error('Unexpected Workflow Error');
    }
  }),
  saveWorkflowFailure: action((state, error) => {
    state.workflowActionError = error;
  }),
  setWorkflowFormatter: action((state, payload) => {
    state.workflowFormatters[payload.workflowKey] = payload.formatter;
  }),
};
